import { padDigit } from './padDigit'
import { DateTime } from 'luxon'

const getMidnightInTimeZone = (
  year: number,
  month: number,
  day: number,
  timeZone: string,
) =>
  DateTime.local()
    .setZone(timeZone)
    .set({ year, month, day, hour: 0, minute: 0, second: 0, millisecond: 0 })

export const getMidnightInTimeZoneFromDateTime = (
  date: DateTime,
  timeZone: string,
) => {
  const year = date.get('year')
  const month = date.get('month')
  const day = date.get('day')

  return getMidnightInTimeZone(year, month, day, timeZone)
}

export const getElapsedSecondsFromMidnight = ({
  clockDate,
  clockHour,
  clockMinute,
  relativeDate,
  timeZone,
}: {
  // Note: clockDate and relativeDate may not always be the same,
  //       in practice, relativeDate would be the Time Card date,
  //       whereas the clockDate may be the Time Card date, or the next day,
  //       if calculating an end time that is on the next day
  //       (e.g. the specified end time is before the start time, 9am -> 4am)
  clockDate: DateTime // The date the clock hour/minutes are on
  clockHour: number
  clockMinute: number
  relativeDate: DateTime // The date we are calculating relative to midnight on
  timeZone: string
}) => {
  const relativeMidnight = getMidnightInTimeZoneFromDateTime(
    relativeDate,
    timeZone,
  )

  const clock = DateTime.local()
    .setZone(timeZone)
    .set({
      year: clockDate.get('year'),
      month: clockDate.get('month'),
      day: clockDate.get('day'),
      hour: clockHour,
      minute: clockMinute,
      second: 0,
      millisecond: 0,
    })

  return clock.diff(relativeMidnight, 'second').toMillis() / 1000
}

export const convertSecondsFromMidnightIntoDateTime = (
  seconds: number,
  dateTime: DateTime,
  timeZone: string,
): DateTime => {
  const year = dateTime.get('year')
  const month = dateTime.get('month')
  const day = dateTime.get('day')

  const midnight = getMidnightInTimeZone(year, month, day, timeZone)

  return midnight.plus({ seconds })
}

// A time (expressed as seconds since midnight on referenceDate) is "next day"
// when it lands on the day after referenceDate — e.g. an end time of 2am on a
// time card whose date is the day before. Mirrors the TimeWidget's own logic.
export const isNextDay = (
  seconds: number,
  referenceDate: DateTime,
  timeZone: string,
): boolean => {
  const dateTime = convertSecondsFromMidnightIntoDateTime(
    seconds,
    referenceDate,
    timeZone,
  )

  return dateTime.hasSame(referenceDate.plus({ days: 1 }), 'day')
}

export const getClockFromElapsedSecondsFromMidnight = (
  seconds: number,
  referenceDate: DateTime,
  timeZone: string,
) => {
  const dateTime = convertSecondsFromMidnightIntoDateTime(
    seconds,
    referenceDate,
    timeZone,
  )

  return { hour: dateTime.get('hour'), minute: dateTime.get('minute') }
}

export const hoursFromSeconds = (seconds: number) =>
  Math.floor(seconds / 60 ** 2)

export const minutesFromSeconds = (
  seconds: number,
  options: { round: 'floor' | 'ceil' } = { round: 'floor' },
) => {
  const minutes = (seconds % 60 ** 2) / 60

  if (options.round === 'floor') return Math.floor(minutes)
  if (options.round === 'ceil') return Math.ceil(minutes)

  return minutes
}

export const secondsToHHMM = (
  seconds: number,
  options?: { language?: string },
) => {
  const hours = hoursFromSeconds(seconds)
  const mins = minutesFromSeconds(seconds, { round: 'ceil' })

  const labels = ['h', 'm']

  return [hours, mins]
    .reduce<string[]>((a, p, i) => {
      // Only include hours/minutes that are > 0
      if (p > 0) {
        // Add appropriate label for part
        const formatted = options?.language
          ? p.toLocaleString(options.language)
          : p
        a.push(`${formatted}${labels[i]}`)
      }

      return a
    }, [])
    .join(' ')
}

export const secondsToTimestamp = (
  seconds: number,
  options?: { language?: string },
) => {
  const hours = hoursFromSeconds(seconds)
  const minutes = minutesFromSeconds(seconds)

  const minutesPart = minutes < 10 ? `0${minutes}` : minutes

  const hoursString = options?.language
    ? hours.toLocaleString(options.language)
    : hours

  return `${hoursString}:${minutesPart}`
}

const secondsTo24HourTimeOfDay = (
  seconds: number,
  referenceDate: DateTime,
  timeZone: string,
) => {
  const { hour, minute } = getClockFromElapsedSecondsFromMidnight(
    seconds,
    referenceDate,
    timeZone,
  )

  return `${hour}:${padDigit(minute)}`
}

const secondsTo12HourTimeOfDay = (
  seconds: number,
  referenceDate: DateTime,
  timeZone: string,
) => {
  const { hour, minute } = getClockFromElapsedSecondsFromMidnight(
    seconds,
    referenceDate,
    timeZone,
  )

  const calcHours = (hours: number) => {
    if (hours > 12) return hours - 12
    if (hours > 0) return hours
    if (hours === 0) return 12

    return ''
  }

  const calcMinutes = (minutes: number) => {
    if (minutes == 0) return ''
    if (minutes < 10) return `:0${minutes}`
    return `:${minutes}`
  }
  const amOrPm = hour < 12 ? 'am' : 'pm'

  return `${calcHours(hour)}${calcMinutes(minute)}${amOrPm}`
}

interface ClockOptions {
  clock: '12' | '24'
}

export const secondsToTimeOfDay = (
  seconds: number,
  referenceDate: DateTime,
  timeZone: string,
  options: ClockOptions = { clock: '12' },
) => {
  return options.clock === '12'
    ? secondsTo12HourTimeOfDay(seconds, referenceDate, timeZone)
    : secondsTo24HourTimeOfDay(seconds, referenceDate, timeZone)
}

export const rangeInTimeOfDay = (
  startAt: number,
  endAt: number,
  referenceDate: DateTime,
  timeZone: string,
  options: ClockOptions = { clock: '12' },
) => {
  const start = secondsToTimeOfDay(startAt, referenceDate, timeZone, options)
  const end = secondsToTimeOfDay(endAt, referenceDate, timeZone, options)

  if (startAt < 0) return ''
  if (endAt < 0) return ''

  return `${start} – ${end}`
}
